import { useState, useEffect, useCallback, useRef } from "react"; // Added useRef
import { v4 as uuidv4 } from "uuid";
import { useLocalStorageSync } from "./useLocalStorageSync";

const MAX_ADDITIONAL_CLOCKS = 2;

const getDerivedFriendlyTimezoneName = (ianaName) => {
  if (!ianaName) return "Unknown";
  const parts = ianaName.split("/");
  return parts[parts.length - 1].replace(/_/g, " ");
};

export function useWorldClocks() {
  const { getItem, setItem } = useLocalStorageSync(["worldClocks"]);
  const [clocks, setClocks] = useState([]);
  const hasLoadedClocksRef = useRef(false);

  useEffect(() => {
    if (hasLoadedClocksRef.current) return;

    const storedClocks = getItem("worldClocks", null);
    if (
      storedClocks &&
      Array.isArray(storedClocks) &&
      storedClocks.length > 0
    ) {
      // Ensure loaded clocks have the new structure
      const processedStoredClocks = storedClocks.map((clock) => ({
        id: clock.id || uuidv4(),
        ianaTimezone: clock.timezone || clock.ianaTimezone, // Handle old 'timezone' key
        userInputFriendlyName:
          clock.userInputFriendlyName ||
          clock.friendlyName ||
          getDerivedFriendlyTimezoneName(clock.timezone || clock.ianaTimezone),
        isLocal: clock.isLocal || false,
      }));
      setClocks(processedStoredClocks);
    } else {
      try {
        const localTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const initialLocalClock = {
          id: uuidv4(),
          ianaTimezone: localTZ,
          userInputFriendlyName:
            getDerivedFriendlyTimezoneName(localTZ) || "Local Time",
          isLocal: true,
        };
        setClocks([initialLocalClock]);
      } catch (error) {
        console.error("Error getting local timezone:", error);
        const fallbackClock = {
          id: uuidv4(),
          ianaTimezone: "UTC",
          userInputFriendlyName: "UTC (Local Fallback)",
          isLocal: true,
        };
        setClocks([fallbackClock]);
      }
    }
    hasLoadedClocksRef.current = true;
  }, [getItem]);

  useEffect(() => {
    if (!hasLoadedClocksRef.current) return;
    if (clocks.length > 0 || localStorage.getItem("worldClocks") !== null) {
      setItem("worldClocks", clocks);
    }
  }, [clocks, setItem]);

  const addClock = useCallback((ianaTimezone, userInputName = "") => {
    setClocks((prevClocks) => {
      const additionalClocksCount = prevClocks.filter((c) => !c.isLocal).length;
      if (additionalClocksCount >= MAX_ADDITIONAL_CLOCKS) {
        console.warn(
          `Cannot add more than ${MAX_ADDITIONAL_CLOCKS} additional clocks.`
        );
        return prevClocks;
      }
      if (prevClocks.some((c) => c.ianaTimezone === ianaTimezone)) {
        console.warn(`Clock with timezone ${ianaTimezone} already exists.`);
        return prevClocks;
      }
      const newClock = {
        id: uuidv4(),
        ianaTimezone: ianaTimezone,
        userInputFriendlyName:
          userInputName || getDerivedFriendlyTimezoneName(ianaTimezone),
        isLocal: false,
      };
      return [...prevClocks, newClock];
    });
  }, []);

  const removeClock = useCallback((clockId) => {
    setClocks((prevClocks) => {
      const clockToRemove = prevClocks.find((c) => c.id === clockId);
      if (clockToRemove && clockToRemove.isLocal) {
        console.warn("Cannot remove the local time clock.");
        return prevClocks;
      }
      return prevClocks.filter((clock) => clock.id !== clockId);
    });
  }, []);

  const updateClock = useCallback(
    (clockId, newIanaTimezone, newUserInputFriendlyName) => {
      setClocks((prevClocks) => {
        // Check for duplicate IANA timezones if it's being changed
        if (
          newIanaTimezone &&
          prevClocks.some(
            (c) => c.id !== clockId && c.ianaTimezone === newIanaTimezone
          )
        ) {
          console.warn(
            `Clock with timezone ${newIanaTimezone} already exists.`
          );
          // Optionally, provide user feedback here
          return prevClocks;
        }
        return prevClocks.map((clock) => {
          if (clock.id === clockId) {
            // Local clock's IANA timezone cannot be changed
            const ianaTzToUpdate = clock.isLocal
              ? clock.ianaTimezone
              : newIanaTimezone || clock.ianaTimezone;
            return {
              ...clock,
              ianaTimezone: ianaTzToUpdate,
              userInputFriendlyName:
                newUserInputFriendlyName !== undefined
                  ? newUserInputFriendlyName
                  : clock.userInputFriendlyName,
            };
          }
          return clock;
        });
      });
    },
    []
  );

  return {
    clocks,
    addClock,
    removeClock,
    updateClock, // Renamed from updateClockTimezone for clarity
    MAX_ADDITIONAL_CLOCKS,
  };
}
